import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { NotificationPreferenceView, NotificationTrigger } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { Toast } from 'widgets';

const NotificationPreferenceQueryKeys = {
    all: ['notification-preferences'] as const,
};

export const NOTIFICATION_GROUPS = [
    {
        id: 'tasks',
        title: 'Tasks',
        description: 'Control notifications about tasks and your task activity.',
        prefix: 'TASK_',
    },
    {
        id: 'withdrawals',
        title: 'Withdrawals',
        description: 'Control notifications about withdrawal requests.',
        prefix: 'WITHDRAWAL_',
    },
    {
        id: 'account-onboarding',
        title: 'Account Onboarding',
        description: 'Control notifications about new account onboarding.',
        prefix: 'ACCOUNT_ONBOARDING_',
    },
    {
        id: 'client-files',
        title: 'Client Files',
        description: 'Control notifications about files uploaded by clients.',
        prefix: 'CLIENT_FILE_',
    },
] as const;

export const groupNotificationPreferences = (preferences: NotificationPreferenceView[]) =>
    NOTIFICATION_GROUPS.map((group) => ({
        ...group,
        preferences: preferences.filter(({ trigger }) => trigger.startsWith(group.prefix)),
    }));

export const useNotificationPreferences = () =>
    useQuery({
        queryKey: NotificationPreferenceQueryKeys.all,
        queryFn: async () => {
            const [response] = await CustomerApi().listNotificationPreferences();
            return response ?? [];
        },
    });

export const useUpdateNotificationPreference = () => {
    const queryClient = useQueryClient();
    const queryKey = NotificationPreferenceQueryKeys.all;

    return useMutation({
        mutationFn: async ({ trigger, enabled }: { trigger: NotificationTrigger; enabled: boolean }) => {
            await CustomerApi().updateNotificationPreference(trigger, { enabled });
        },
        onMutate: async ({ trigger, enabled }) => {
            await queryClient.cancelQueries({ queryKey });
            const previousPreference = queryClient
                .getQueryData<NotificationPreferenceView[]>(queryKey)
                ?.find((preference) => preference.trigger === trigger);

            queryClient.setQueryData<NotificationPreferenceView[]>(queryKey, (currentPreferences) =>
                currentPreferences?.map((preference) =>
                    preference.trigger === trigger ? { ...preference, enabled } : preference,
                ),
            );

            return { previousPreference };
        },
        onError: (e: unknown, _variables, context) => {
            const previousPreference = context?.previousPreference;

            if (previousPreference) {
                queryClient.setQueryData<NotificationPreferenceView[]>(queryKey, (currentPreferences) =>
                    currentPreferences?.map((preference) =>
                        preference.trigger === previousPreference.trigger ? previousPreference : preference,
                    ),
                );
            }
            Toast.forError(e, 'Failed to update notification preference.');
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });
};
